import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, LayoutGrid, ChevronRight, ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import SectionNav from '../../../components/layout/SectionNav';
import { discoverTabs } from '../../../nav/publicNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

const METRICS = [
  { key: 'pe', label: 'P/E Ratio' },
  { key: 'pb', label: 'P/B Ratio' },
  { key: 'roe', label: 'ROE %' },
  { key: 'opm', label: 'OPM %' },
  { key: 'fii_change', label: 'FII Change' },
];

const LEVELS = [
  { key: 'macro_sector', label: 'Macro Sectors' },
  { key: 'sector', label: 'Sectors' },
  { key: 'industry', label: 'Industries' },
];

function getColor(value, metric) {
  if (value == null) return 'hsl(var(--muted))';
  if (metric === 'fii_change') {
    if (value > 1) return '#22c55e';
    if (value > 0) return '#86efac';
    if (value > -1) return '#fca5a5';
    return '#ef4444';
  }
  if (metric === 'roe' || metric === 'opm' || metric === 'npm') {
    if (value > 20) return '#22c55e';
    if (value > 12) return '#86efac';
    if (value > 5) return '#fbbf24';
    return '#ef4444';
  }
  if (value < 15) return '#22c55e';
  if (value < 25) return '#86efac';
  if (value < 40) return '#fbbf24';
  return '#ef4444';
}

function CustomContent({ x, y, width, height, name, avg_metric, company_count, metric }) {
  if (width < 40 || height < 30) return null;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx={4}
        fill={getColor(avg_metric, metric)} fillOpacity={0.85}
        stroke="hsl(var(--background))" strokeWidth={2} />
      {width > 60 && (
        <>
          <text x={x + width / 2} y={y + height / 2 - 6} textAnchor="middle"
            className="fill-white text-[10px] font-semibold" style={{ fontSize: Math.min(12, width / 8) }}>
            {name?.length > width / 7 ? name.slice(0, Math.floor(width / 7)) + '…' : name}
          </text>
          <text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle"
            className="fill-white/80 text-[9px]" style={{ fontSize: Math.min(10, width / 9) }}>
            {avg_metric != null ? avg_metric.toFixed(1) : '—'} · {company_count} co.
          </text>
        </>
      )}
    </g>
  );
}

function HeatmapTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-popover text-popover-foreground border border-border rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold">{d?.name}</p>
      <p>Companies: {d?.company_count}</p>
      <p>Avg Metric: {d?.avg_metric != null ? d.avg_metric.toFixed(2) : '—'}</p>
      {d?.total_market_cap && <p>Total Mcap: ₹{(d.total_market_cap / 1e12).toFixed(1)}T</p>}
    </div>
  );
}

export default function SectorHeatmapPage() {
  const navigate = useNavigate();
  const [metric, setMetric] = useState('pe');
  const [level, setLevel] = useState('sector');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchHeatmap = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDrilldown(null);
    try {
      const res = await fetch(`${API}/heatmap?metric=${metric}&level=${level}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData((json.groups || []).map(g => ({
        ...g,
        size: g.total_market_cap || g.company_count * 1000,
      })));
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [metric, level]);

  useEffect(() => { fetchHeatmap(); }, [fetchHeatmap]);

  const handleDrilldown = async (groupName) => {
    if (metric === 'fii_change') return;
    setDrilldown(groupName);
    setDrillLoading(true);
    try {
      const res = await fetch(`${API}/heatmap/drilldown?metric=${metric}&group_name=${encodeURIComponent(groupName)}&level=${level}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setDrillData(json.companies || []);
    } catch (e) {
      setError(e.message);
    }
    setDrillLoading(false);
  };

  return (
    <div className="space-y-4">
      <SectionNav tabs={discoverTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-emerald-500" /> Sector Heatmap
        </h1>
        <p className="text-xs text-muted-foreground">Visual map of sectors by performance and valuation metrics</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1">
          {METRICS.map(m => (
            <Button key={m.key} size="sm" variant={metric === m.key ? 'default' : 'outline'}
              className={`text-xs h-7 ${metric === m.key ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={() => setMetric(m.key)}>
              {m.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-1 ml-auto">
          {LEVELS.map(l => (
            <Button key={l.key} size="sm" variant={level === l.key ? 'default' : 'outline'}
              className={`text-xs h-7 ${level === l.key ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={() => setLevel(l.key)}>
              {l.label}
            </Button>
          ))}
        </div>
      </div>

      {drilldown && (
        <div className="flex items-center gap-2 text-sm">
          <Button variant="ghost" size="sm" onClick={() => setDrilldown(null)}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
          </Button>
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
          <span className="font-medium">{drilldown}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : drilldown ? (
        drillLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-emerald-800 text-white">
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Company</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Symbol</th>
                  <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">{metric.toUpperCase()}</th>
                  <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Market Cap</th>
                  <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Price</th>
                </tr>
              </thead>
              <tbody>
                {drillData.map((c, i) => (
                  <tr key={i} className="border-b border-muted/20 hover:bg-muted/20 cursor-pointer"
                    onClick={() => c.symbol && navigate(`/public/india-company/${c.symbol}`)}>
                    <td className="py-2 px-3 font-medium">{c.company_name}</td>
                    <td className="py-2 px-3">{c.symbol || '—'}</td>
                    <td className="py-2 px-3 text-right font-semibold"
                      style={{ color: getColor(c.metric_value, metric) }}>
                      {c.metric_value ?? '—'}
                    </td>
                    <td className="py-2 px-3 text-right text-muted-foreground">
                      {c.market_cap ? `₹${(c.market_cap / 1e10).toFixed(0)}B` : '—'}
                    </td>
                    <td className="py-2 px-3 text-right">₹{c.price ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <Card>
          <CardContent className="p-2">
            <ResponsiveContainer width="100%" height={500}>
              <Treemap
                data={data}
                dataKey="size"
                nameKey="name"
                content={<CustomContent metric={metric} />}
                onClick={(node) => node?.name && handleDrilldown(node.name)}
                isAnimationActive={false}
              >
                <Tooltip content={<HeatmapTooltip />} />
              </Treemap>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-4 text-[10px] text-muted-foreground justify-center">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#22c55e]" /> Good</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#86efac]" /> Fair</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#fbbf24]" /> Caution</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#ef4444]" /> Risk</span>
      </div>

      {/* Sector relative-strength strip (vs Nifty 50) */}
      <SectorRSStrip />
    </div>
  );
}


function SectorRSStrip() {
  const [rs, setRs] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/sector-rs`)
      .then((res) => { if (!cancelled) setRs(res.data); })
      .catch(() => { if (!cancelled) setRs(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  if (loading || !rs || !rs.results || !rs.results.length) return null;

  const winners = rs.results.slice(0, 5);
  const losers = rs.results.slice().reverse().slice(0, 5);

  const Row = ({ icon: Icon, label, accent, items }) => (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        <Icon className={`w-3 h-3 ${accent}`} /> {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((i) => {
          const v = i.vs_nifty?.['3m'];
          return (
            <span key={i.symbol}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px]
                ${v != null && v >= 0
                  ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                  : 'border-red-500/40 text-red-500'}`}
            >
              <span className="font-medium">{i.display_name}</span>
              <span>{v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}</span>
            </span>
          );
        })}
      </div>
    </div>
  );

  return (
    <Card>
      <CardContent className="p-3 space-y-3">
        <div className="text-[11px] text-muted-foreground">
          Sector relative strength vs Nifty 50 (3-month). Nifty 3M: {' '}
          <span className="font-medium">
            {rs.nifty_returns?.['3m'] == null ? '—' : `${rs.nifty_returns['3m']}%`}
          </span>
        </div>
        <Row icon={TrendingUp} label="Outperformers" accent="text-emerald-500" items={winners} />
        <Row icon={TrendingDown} label="Underperformers" accent="text-red-500" items={losers} />
      </CardContent>
    </Card>
  );
}
